import { useEffect, useState } from 'react';
import { alphaApi, type Notification } from '../api/alpha';

type ShellNotification = Notification & { severity?: 'info' | 'success' | 'warning' | 'error' };

function severityFrom(n: Notification): ShellNotification['severity'] {
  const p = (n.priority || '').toLowerCase();
  if (p === 'critical' || p === 'high' || p === 'error') return 'error';
  if (p === 'warning' || p === 'medium') return 'warning';
  if (p === 'success' || p === 'low') return 'success';
  return 'info';
}

const severityStyle: Record<string, string> = {
  info: 'text-[var(--shell-accent)]',
  success: 'text-[var(--shell-success)]',
  warning: 'text-[var(--shell-warning)]',
  error: 'text-[var(--shell-danger)]',
};

/**
 * Notification center shell — unread/read + severity.
 * Placeholder provider via alphaApi; failures show empty state (no fake alerts).
 */
export function NotificationCenter() {
  const [items, setItems] = useState<ShellNotification[]>([]);
  const [open, setOpen] = useState(false);
  const [showRead, setShowRead] = useState(false);

  const load = async () => {
    try {
      const raw = await alphaApi.notifications(!showRead);
      setItems(raw.map((n) => ({ ...n, severity: severityFrom(n) })));
    } catch {
      setItems([]);
    }
  };

  useEffect(() => {
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, [showRead]);

  const markRead = async (id: string) => {
    try {
      await alphaApi.markRead(id);
      await load();
    } catch {
      /* ignore */
    }
  };

  const unreadCount = items.filter((n) => !n.read).length;

  return (
    <div className="relative">
      <button
        type="button"
        aria-label={`Notifications${unreadCount ? `, ${unreadCount} unread` : ''}`}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="relative min-h-[44px] rounded-[var(--shell-radius-md)] border border-[var(--shell-border)] bg-[var(--shell-surface-elevated)] px-3 py-2 text-sm font-medium text-[var(--shell-fg)] hover:bg-[var(--shell-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--shell-focus-ring)]"
      >
        Alerts
        {unreadCount > 0 && (
          <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-[var(--shell-danger)] px-1 text-xs font-bold text-white">
            {unreadCount}
          </span>
        )}
      </button>
      {open && (
        <div
          role="region"
          aria-label="Notification list"
          className="absolute right-0 z-50 mt-2 w-80 rounded-[var(--shell-radius-md)] border border-[var(--shell-border)] bg-[var(--shell-surface-elevated)] shadow-lg"
        >
          <div className="flex items-center justify-between border-b border-[var(--shell-border)] px-3 py-2">
            <p className="text-xs font-semibold text-[var(--shell-fg)]">Notifications</p>
            <button
              type="button"
              className="text-xs text-[var(--shell-accent)] hover:underline"
              onClick={() => setShowRead((v) => !v)}
            >
              {showRead ? 'Unread only' : 'Include read'}
            </button>
          </div>
          {items.length === 0 ? (
            <p className="p-4 text-sm text-[var(--shell-fg-muted)]">No notifications</p>
          ) : (
            <ul className="max-h-80 divide-y divide-[var(--shell-border)] overflow-auto">
              {items.map((n) => (
                <li key={n.id} className={`p-4 ${n.read ? 'opacity-70' : ''}`}>
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-medium text-[var(--shell-fg)]">{n.title}</p>
                    <span className={`shrink-0 text-[10px] font-semibold uppercase ${severityStyle[n.severity ?? 'info']}`}>
                      {n.severity}
                    </span>
                  </div>
                  {n.body && <p className="mt-1 text-xs text-[var(--shell-fg-muted)]">{n.body}</p>}
                  <div className="mt-2 flex gap-3 text-xs">
                    {!n.read && (
                      <button
                        type="button"
                        onClick={() => markRead(n.id)}
                        className="font-medium text-[var(--shell-accent)] hover:underline"
                      >
                        Mark read
                      </button>
                    )}
                    {n.read && <span className="text-[var(--shell-fg-subtle)]">Read</span>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
